import React, { useState, useEffect } from 'react';
import {
  X,
  Save,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  Send,
  HelpCircle,
  Key,
  Shield,
  Server,
  Mail,
  UserCheck,
  ExternalLink,
} from 'lucide-react';
import { EmailSettings, SenderAccount } from '../types';

interface EmailConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: EmailSettings;
  electionTitle: string;
  onSaveSettings: (updated: EmailSettings) => Promise<void> | void;
  onTestAccount: (account: SenderAccount) => Promise<{ success: boolean; message: string }>;
}

export const EmailConfigModal: React.FC<EmailConfigModalProps> = ({
  isOpen,
  onClose,
  settings,
  electionTitle,
  onSaveSettings,
  onTestAccount,
}) => {
  const [localSettings, setLocalSettings] = useState<EmailSettings>(settings);
  const [selectedAuthTab, setSelectedAuthTab] = useState<'app_password' | 'oauth2' | 'smtp'>(
    settings.activeAuthOption || 'app_password'
  );

  // New Account inline form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newLimit, setNewLimit] = useState(500);
  const [newNotes, setNewNotes] = useState('');

  // Account testing state
  const [testingAccountId, setTestingAccountId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ accountId: string; success: boolean; message: string } | null>(null);

  // Synchronize local settings whenever modal opens or parent settings update
  useEffect(() => {
    if (isOpen) {
      setLocalSettings(settings);
      setSelectedAuthTab(settings.activeAuthOption || 'app_password');
      setTestResult(null);
    }
  }, [isOpen, settings]);

  if (!isOpen) return null;

  const totalQuota = localSettings.accounts.reduce((acc, a) => acc + (a.isActive ? a.dailyLimit : 0), 0);
  const totalSentToday = localSettings.accounts.reduce((acc, a) => acc + (a.isActive ? a.sentToday : 0), 0);

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      alert('Please enter a valid Gmail address.');
      return;
    }

    const newAcc: SenderAccount = {
      id: `acc_${Date.now()}`,
      email: newEmail.trim(),
      authType: selectedAuthTab,
      appPassword: newPassword.trim() || '•••• •••• •••• ••••',
      dailyLimit: Number(newLimit) || 500,
      sentToday: 0,
      isActive: true,
      status: 'active',
      notes: newNotes.trim() || `Sender Account #${localSettings.accounts.length + 1}`,
    };

    const updated: EmailSettings = {
      ...localSettings,
      activeAuthOption: selectedAuthTab,
      accounts: [...localSettings.accounts, newAcc],
    };

    setLocalSettings(updated);
    await onSaveSettings(updated);

    setNewEmail('');
    setNewPassword('');
    setNewNotes('');
    setShowAddForm(false);
  };

  const handleToggleAccountActive = async (id: string) => {
    const updated: EmailSettings = {
      ...localSettings,
      activeAuthOption: selectedAuthTab,
      accounts: localSettings.accounts.map((a) =>
        a.id === id ? { ...a, isActive: !a.isActive, status: !a.isActive ? 'active' : 'idle' } : a
      ),
    };
    setLocalSettings(updated);
    await onSaveSettings(updated);
  };

  const handleResetAccountCount = async (id: string) => {
    const updated: EmailSettings = {
      ...localSettings,
      activeAuthOption: selectedAuthTab,
      accounts: localSettings.accounts.map((a) => (a.id === id ? { ...a, sentToday: 0, status: 'active' } : a)),
    };
    setLocalSettings(updated);
    await onSaveSettings(updated);
  };

  const handleResetAllCounts = async () => {
    if (confirm('Reset sent counters for all configured sender accounts?')) {
      const updated: EmailSettings = {
        ...localSettings,
        activeAuthOption: selectedAuthTab,
        accounts: localSettings.accounts.map((a) => ({ ...a, sentToday: 0, status: 'active' })),
      };
      setLocalSettings(updated);
      await onSaveSettings(updated);
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (confirm('Are you sure you want to remove this sender account from the rotation pool?')) {
      const updated: EmailSettings = {
        ...localSettings,
        activeAuthOption: selectedAuthTab,
        accounts: localSettings.accounts.filter((a) => a.id !== id),
      };
      setLocalSettings(updated);
      await onSaveSettings(updated);
    }
  };

  const handleRunTest = async (acc: SenderAccount) => {
    setTestingAccountId(acc.id);
    setTestResult(null);
    try {
      const res = await onTestAccount(acc);
      setTestResult({ accountId: acc.id, success: res.success, message: res.message });
    } catch (err: any) {
      setTestResult({ accountId: acc.id, success: false, message: err.message || 'Test send failed' });
    }
    setTestingAccountId(null);
  };

  const handleSave = async () => {
    await onSaveSettings({
      ...localSettings,
      activeAuthOption: selectedAuthTab,
    });
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#171619',
          border: '1px solid #2D2C31',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '920px',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
          overflow: 'hidden',
          color: '#FFFFFF',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '20px 26px',
            borderBottom: '1px solid #2D2C31',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                backgroundColor: 'rgba(255, 153, 0, 0.12)',
                border: '1px solid rgba(255, 153, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FF9900',
              }}
            >
              <Mail size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>
                Email Delivery & Gmail Account Pool Configuration
              </h3>
              <p style={{ fontSize: '12px', color: '#8E8E93', margin: '2px 0 0 0' }}>
                Connect Gmail accounts, manage 500-quota rotation, and configure sender identity.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#8E8E93',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '8px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div style={{ padding: '22px 26px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* SECTION 1: SENDER IDENTITY */}
          <div>
            <div style={{ fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: '#FF9900', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <UserCheck size={16} /> 1. Sender Identity & Election Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#A1A1AA', marginBottom: '6px' }}>
                  Sender Display Name
                </label>
                <input
                  type="text"
                  value={localSettings.senderName}
                  onChange={(e) => setLocalSettings({ ...localSettings, senderName: e.target.value })}
                  placeholder="e.g. Law Students' Society Electoral Commission"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#A1A1AA', marginBottom: '6px' }}>
                  Reply-To Email Address
                </label>
                <input
                  type="email"
                  value={localSettings.replyTo}
                  onChange={(e) => setLocalSettings({ ...localSettings, replyTo: e.target.value })}
                  placeholder="e.g. bukola@electpoll.com"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#A1A1AA', marginBottom: '6px' }}>
                  Voting Portal Direct Link
                </label>
                <input
                  type="url"
                  value={localSettings.loginUrl}
                  onChange={(e) => setLocalSettings({ ...localSettings, loginUrl: e.target.value })}
                  placeholder="https://lssui.electpoll.com/"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#A1A1AA', marginBottom: '6px' }}>
                  Support / Helpdesk Email
                </label>
                <input
                  type="email"
                  value={localSettings.supportEmail}
                  onChange={(e) => setLocalSettings({ ...localSettings, supportEmail: e.target.value })}
                  placeholder="bukola@electpoll.com"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#A1A1AA', marginBottom: '6px' }}>
                  Voting Times
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={localSettings.votingStartTime}
                    onChange={(e) => setLocalSettings({ ...localSettings, votingStartTime: e.target.value })}
                    placeholder="8:00 a.m."
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      backgroundColor: '#1E1D21',
                      border: '1px solid #2D2C31',
                      color: '#FFFFFF',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <input
                    type="text"
                    value={localSettings.votingEndTime}
                    onChange={(e) => setLocalSettings({ ...localSettings, votingEndTime: e.target.value })}
                    placeholder="4:00 p.m."
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      backgroundColor: '#1E1D21',
                      border: '1px solid #2D2C31',
                      color: '#FFFFFF',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#A1A1AA', marginBottom: '6px' }}>
                  Election Date
                </label>
                <input
                  type="text"
                  value={localSettings.electionDate}
                  onChange={(e) => setLocalSettings({ ...localSettings, electionDate: e.target.value })}
                  placeholder="22nd September 2026"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          </div>

          {/* SECTION 2: GMAIL CONNECTION OPTIONS */}
          <div>
            <div style={{ fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: '#FF9900', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Key size={16} /> 2. Gmail Connection Options
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
              {/* Option 1: App Password */}
              <div
                onClick={() => setSelectedAuthTab('app_password')}
                style={{
                  backgroundColor: selectedAuthTab === 'app_password' ? 'rgba(255, 153, 0, 0.08)' : '#1C1B20',
                  border: selectedAuthTab === 'app_password' ? '1.5px solid #FF9900' : '1px solid #2D2C31',
                  borderRadius: '14px',
                  padding: '16px',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Key size={16} color="#FF9900" />
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#FFFFFF' }}>
                      Google App Password
                    </span>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 800, backgroundColor: '#FF9900', color: '#171619', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase' }}>
                    Recommended
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: '#8E8E93', lineHeight: 1.5, margin: 0 }}>
                  Immediate setup with zero Google Cloud console approval. Requires enabling 2FA on Gmail, generating a 16-character App Password, and adding it to the pool.
                </p>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#FF9900', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>Fastest for election committee dispatch</span>
                </div>
              </div>

              {/* Option 2: OAuth 2.0 */}
              <div
                onClick={() => setSelectedAuthTab('oauth2')}
                style={{
                  backgroundColor: selectedAuthTab === 'oauth2' ? 'rgba(56, 189, 248, 0.08)' : '#1C1B20',
                  border: selectedAuthTab === 'oauth2' ? '1.5px solid #38BDF8' : '1px solid #2D2C31',
                  borderRadius: '14px',
                  padding: '16px',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Shield size={16} color="#38BDF8" />
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#FFFFFF' }}>
                      Google OAuth 2.0
                    </span>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: 'rgba(56, 189, 248, 0.2)', color: '#38BDF8', padding: '2px 6px', borderRadius: '4px' }}>
                    Gmail API
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: '#8E8E93', lineHeight: 1.5, margin: 0 }}>
                  Authorizes via Google Cloud OAuth Client ID & Secret with <code>gmail.send</code> scope. Provides token-based security and direct REST API transmission.
                </p>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#38BDF8' }}>
                  Enterprise Google Workspace integration
                </div>
              </div>

              {/* Option 3: Custom SMTP Relay */}
              <div
                onClick={() => setSelectedAuthTab('smtp')}
                style={{
                  backgroundColor: selectedAuthTab === 'smtp' ? 'rgba(16, 185, 129, 0.08)' : '#1C1B20',
                  border: selectedAuthTab === 'smtp' ? '1.5px solid #10B981' : '1px solid #2D2C31',
                  borderRadius: '14px',
                  padding: '16px',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Server size={16} color="#10B981" />
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#FFFFFF' }}>
                      External SMTP / Relay
                    </span>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981', padding: '2px 6px', borderRadius: '4px' }}>
                    Custom Relay
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: '#8E8E93', lineHeight: 1.5, margin: 0 }}>
                  Connect custom domain relays (SendGrid, Resend, or University mail server) with host, port (465/587), and API authentication.
                </p>
                <div style={{ marginTop: '10px', fontSize: '11px', color: '#10B981' }}>
                  Custom domain support (@ui.edu.ng)
                </div>
              </div>
            </div>

            {/* Quick Step Guide for Selected Option */}
            <div
              style={{
                marginTop: '12px',
                backgroundColor: '#131215',
                padding: '12px 16px',
                borderRadius: '10px',
                border: '1px solid #2D2C31',
                fontSize: '12px',
                color: '#A1A1AA',
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontWeight: 700, color: '#FFFFFF', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <HelpCircle size={14} color="#FF9900" /> How to generate a Google App Password for dispatch:
              </div>
              1. Log in to your Gmail account and go to <strong>Google Account Security</strong>.<br />
              2. Ensure <strong>2-Step Verification</strong> is ON.<br />
              3. Search for <strong>App Passwords</strong> (or visit <code>myaccount.google.com/apppasswords</code>).<br />
              4. Enter an app name (e.g., <em>Electpoll Dispatcher</em>) and click <strong>Create</strong>.<br />
              5. Copy the 16-character password and paste it into the account pool below.
            </div>
          </div>

          {/* SECTION 3: MULTI-ACCOUNT SENDER POOL & 500 DAILY LIMIT AUTO-ROTATION */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: '#FF9900', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <RotateCcw size={16} /> 3. Sender Accounts Pool & 500 Daily Limit Auto-Rotation
                </div>
                <div style={{ fontSize: '12px', color: '#8E8E93', marginTop: '2px' }}>
                  Total Capacity: <strong style={{ color: '#FFFFFF' }}>{totalQuota - totalSentToday}</strong> remaining today ({totalSentToday} of {totalQuota} used)
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleResetAllCounts}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #2D2C31',
                    color: '#8E8E93',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                  title="Reset today's sent counters across all accounts"
                >
                  <RotateCcw size={13} /> Reset Daily Counters
                </button>

                <button
                  type="button"
                  onClick={() => setShowAddForm(!showAddForm)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    backgroundColor: '#FF9900',
                    color: '#171619',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Plus size={14} /> Add Sender Account
                </button>
              </div>
            </div>

            {/* Why Account Rotation Callout */}
            <div
              style={{
                backgroundColor: 'rgba(255, 153, 0, 0.06)',
                border: '1px solid rgba(255, 153, 0, 0.25)',
                padding: '12px 16px',
                borderRadius: '10px',
                marginBottom: '16px',
                fontSize: '12px',
                color: '#D1D5DB',
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: '#FF9900' }}>Why Multi-Account 500 Limit Auto-Rotation?</strong> Standard free Gmail enforces an unyielding limit of <strong>500 recipients/24 hours</strong>. If an election has 1,200 voters, sending from a single account would fail halfway. By adding multiple accounts, Electpoll automatically switches to Account 2 once Account 1 reaches 500, then Account 3, guaranteeing continuous delivery without quota blocks.
            </div>

            {/* Add Account Inline Form */}
            {showAddForm && (
              <form
                onSubmit={handleAddAccount}
                style={{
                  backgroundColor: '#1C1B20',
                  padding: '18px 20px',
                  borderRadius: '12px',
                  border: '1px solid #FF9900',
                  marginBottom: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#FFFFFF' }}>
                  Add New Sender Account to Pool
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#A1A1AA', marginBottom: '4px' }}>
                      Gmail / Sender Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="e.g. lss.elections.pool2@gmail.com"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '13px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#A1A1AA', marginBottom: '4px' }}>
                      Google App Password (16 chars) *
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="abcd efgh ijkl mnop"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '13px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#A1A1AA', marginBottom: '4px' }}>
                      Daily Sending Limit (Default: 500)
                    </label>
                    <input
                      type="number"
                      min={10}
                      max={2000}
                      value={newLimit}
                      onChange={(e) => setNewLimit(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '13px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', color: '#A1A1AA', marginBottom: '4px' }}>
                      Purpose / Label
                    </label>
                    <input
                      type="text"
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      placeholder="e.g. Backup Dispatcher for 500+ voters"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: '#0E0D0F',
                        border: '1px solid #2D2C31',
                        color: '#FFFFFF',
                        fontSize: '13px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    style={{
                      padding: '7px 14px',
                      borderRadius: '6px',
                      backgroundColor: '#2D2C31',
                      color: '#A1A1AA',
                      border: 'none',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '7px 16px',
                      borderRadius: '6px',
                      backgroundColor: '#FF9900',
                      color: '#171619',
                      border: 'none',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Save & Add to Pool
                  </button>
                </div>
              </form>
            )}

            {/* Configured Accounts Pool List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {localSettings.accounts.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#8E8E93', backgroundColor: '#131215', borderRadius: '10px' }}>
                  No sender accounts configured yet. Add at least one Gmail account to enable live email delivery.
                </div>
              ) : (
                localSettings.accounts.map((acc, index) => {
                  const pct = Math.min(100, Math.round((acc.sentToday / acc.dailyLimit) * 100));
                  const isExhausted = acc.sentToday >= acc.dailyLimit;

                  return (
                    <div
                      key={acc.id}
                      style={{
                        backgroundColor: '#1E1D21',
                        border: isExhausted
                          ? '1px solid #EF4444'
                          : acc.isActive
                          ? '1px solid #2D2C31'
                          : '1px dashed #3F3F46',
                        borderRadius: '12px',
                        padding: '14px 18px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              backgroundColor: '#2D2C31',
                              color: '#A1A1AA',
                              fontSize: '11px',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            #{index + 1}
                          </span>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <strong style={{ fontSize: '14px', color: '#FFFFFF' }}>{acc.email}</strong>
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: isExhausted
                                    ? 'rgba(239, 68, 68, 0.2)'
                                    : acc.isActive
                                    ? 'rgba(16, 185, 129, 0.15)'
                                    : 'rgba(161, 161, 170, 0.15)',
                                  color: isExhausted ? '#EF4444' : acc.isActive ? '#10B981' : '#A1A1AA',
                                }}
                              >
                                {isExhausted ? 'Quota Reached (500/500)' : acc.isActive ? 'Active in Pool' : 'Paused'}
                              </span>
                            </div>
                            {acc.notes && (
                              <div style={{ fontSize: '11px', color: '#8E8E93', marginTop: '2px' }}>
                                {acc.notes}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Account Actions */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            type="button"
                            disabled={testingAccountId === acc.id}
                            onClick={() => handleRunTest(acc)}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '6px',
                              backgroundColor: 'rgba(56, 189, 248, 0.12)',
                              border: '1px solid rgba(56, 189, 248, 0.3)',
                              color: '#38BDF8',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <Send size={11} />
                            <span>{testingAccountId === acc.id ? 'Sending...' : 'Test'}</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleResetAccountCount(acc.id)}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '6px',
                              backgroundColor: '#2D2C31',
                              border: 'none',
                              color: '#A1A1AA',
                              fontSize: '11px',
                              cursor: 'pointer',
                            }}
                            title="Reset sent counter to 0"
                          >
                            Reset
                          </button>

                          <button
                            type="button"
                            onClick={() => handleToggleAccountActive(acc.id)}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '6px',
                              backgroundColor: acc.isActive ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                              border: 'none',
                              color: acc.isActive ? '#EF4444' : '#10B981',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {acc.isActive ? 'Pause' : 'Activate'}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDeleteAccount(acc.id)}
                            style={{
                              padding: '5px 8px',
                              borderRadius: '6px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#6B7280',
                              cursor: 'pointer',
                            }}
                            title="Delete Account"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Quota Progress Bar */}
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#A1A1AA', marginBottom: '4px' }}>
                          <span>
                            Daily Quota Progress: <strong style={{ color: isExhausted ? '#EF4444' : '#FF9900' }}>{acc.sentToday}</strong> / {acc.dailyLimit} sent
                          </span>
                          <span>{acc.dailyLimit - acc.sentToday} remaining today</span>
                        </div>
                        <div style={{ height: '6px', backgroundColor: '#0E0D0F', borderRadius: '3px', overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${pct}%`,
                              height: '100%',
                              backgroundColor: isExhausted ? '#EF4444' : pct > 80 ? '#FF9900' : '#10B981',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                      </div>

                      {/* Test feedback alert */}
                      {testResult && testResult.accountId === acc.id && (
                        <div
                          style={{
                            marginTop: '10px',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: testResult.success ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                            border: testResult.success ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid rgba(239, 68, 68, 0.35)',
                            color: testResult.success ? '#10B981' : '#EF4444',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          {testResult.success ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                          <span>{testResult.message}</span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '16px 26px',
            borderTop: '1px solid #2D2C31',
            backgroundColor: '#141316',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: '12px', color: '#8E8E93' }}>
            Rotation queue dispatches up to 500 emails per active account before seamlessly switching.
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '9px 16px',
                borderRadius: '8px',
                backgroundColor: '#1E1D21',
                border: '1px solid #2D2C31',
                color: '#A1A1AA',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              style={{
                padding: '9px 18px',
                borderRadius: '8px',
                backgroundColor: '#FF9900',
                border: 'none',
                color: '#171619',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Save size={15} /> Save Delivery Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
